/**
 * Plan Calculator Utility
 * Mirrors the backend PlanCalculatorService.calculate_plan logic exactly
 */

export interface ApplicationData {
  business_type?: string
  business_category?: string
  revenue_range?: string
  revenue_range_aum?: string
  is_solopreneur?: boolean | string
  provides_services_in_more_than_three_states?: boolean
}

/**
 * Calculate the appropriate subscription plan based on user application data
 * This logic must match PlanCalculatorService.calculate_plan exactly
 */
export const calculatePlan = (application: ApplicationData): string | null => {
  // Default plan if no specific conditions are met
  let plan: string = 'professional'

  // Check if category is professional
  if (application.business_category === 'professional') {
    return 'professional'
  }

  // Check if it's a company_provider with category 'other'
  if (application.business_type === 'company_provider' && application.business_category === 'other') {
    // Return null for company_provider with category 'other' to force admin selection
    return null
  }

  // Check business type
  switch (application.business_type) {
    case 'company_seeker':
      // For company seekers, check the business category
      if (
        application.business_category === 'corporation' ||
        application.business_category === 'government' ||
        application.business_category === 'nonprofit' ||
        application.business_category === 'education' ||
        application.business_category === 'other'
      ) {
        // Check revenue range for corporation, government, nonprofit, or other seekers
        switch (application.revenue_range) {
          case '50m_100m':
          case '100m_500m':
          case '500m_1b':
          case '1b_5b':
          case '5b_10b':
          case 'over_10b':
            plan = 'elite_premium'
            break
          case '10m_50m':
            plan = 'elite_plus'
            break
          default:
            // For 5m_10m or below
            plan = 'elite'
        }
      } else if (application.business_category === 'emerging') {
        // For emerging businesses, check revenue range and if solopreneur
        const isSolopreneur = application.is_solopreneur === true || application.is_solopreneur === 'yes'
        if (application.revenue_range === 'under_1m' && isSolopreneur) {
          plan = 'solopreneur'
        } else {
          // Check revenue range for other emerging businesses
          switch (application.revenue_range) {
            case '50m_100m':
            case '100m_500m':
            case '500m_1b':
            case '1b_5b':
            case '5b_10b':
            case 'over_10b':
              plan = 'elite_premium'
              break
            case '10m_50m':
              plan = 'elite_plus'
              break
            default:
              // For 5m_10m or below
              plan = 'elite'
          }
        }
      }
      break

    case 'company_provider':
      // For company providers, check the business category
      if (
        application.business_category === 'corporation' ||
        application.business_category === 'government' ||
        application.business_category === 'nonprofit' ||
        application.business_category === 'education'
      ) {
        // Check if they provide services in more than 3 states in the US
        if (application.provides_services_in_more_than_three_states) {
          plan = 'national_corp'
        } else {
          plan = 'regional_corp'
        }
      } else if (application.business_category === 'financial_services') {
        // For financial services, check the revenue range or AUM
        switch (application.revenue_range_aum) {
          case '100m_500m':
          case '500m_1b':
          case '1b_5b':
          case '5b_10b':
          case 'over_10b':
            // For $100M or higher, set to platinum
            plan = 'investors_wealth_management_platinum'
            break
          case '50m_100m':
            // For $50M to $100M, set to gold
            plan = 'investors_wealth_management_gold'
            break
          default:
            // For $10M to $50M or below, set to silver
            plan = 'investors_wealth_management_silver'
        }
      } else if (application.business_category === 'emerging') {
        // For emerging businesses, check revenue range and if solopreneur
        const isSolopreneur = application.is_solopreneur === true || application.is_solopreneur === 'yes'
        if (application.revenue_range === 'under_1m' && isSolopreneur) {
          plan = 'solopreneur'
        } else {
          // Check revenue range for other emerging businesses
          switch (application.revenue_range) {
            case '50m_100m':
            case '100m_500m':
            case '500m_1b':
            case '1b_5b':
            case '5b_10b':
            case 'over_10b':
              plan = 'elite_premium'
              break
            case '10m_50m':
              plan = 'elite_plus'
              break
            default:
              // For 5m_10m or below
              plan = 'elite'
          }
        }
      }
      break

    case 'corporation_provider':
      // For corporations, check size based on revenue range
      if (application.revenue_range === 'Over $1B') {
        plan = 'national_corp'
      } else {
        plan = 'regional_corp'
      }
      break

    case 'emerging_business':
      // For emerging businesses, check if solopreneur
      const isSolopreneur = application.is_solopreneur === true || application.is_solopreneur === 'yes'
      if (isSolopreneur) {
        plan = 'solopreneur'
      } else {
        // Check revenue range for other emerging businesses
        switch (application.revenue_range) {
          case 'Under $1M':
            plan = 'elite'
            break
          case '$1M - $10M':
            plan = 'elite_plus'
            break
          default:
            plan = 'elite_premium'
        }
      }
      break

    case 'financial_services':
      // Financial services typically get elite_premium
      plan = 'elite_premium'
      break

    case 'non_profit_organization':
      // Non-profits typically get elite
      plan = 'elite'
      break

    case 'education':
      // Education organizations get the same plan as non-profits (elite)
      plan = 'elite'
      break
  }

  // Return the calculated plan
  return plan
}

/**
 * Get available plans for an application
 * Returns the calculated plan or null if admin selection is required
 */
export const getAvailablePlans = (application: ApplicationData): string[] => {
  const plan = calculatePlan(application)
  
  // If plan is null (requires admin selection), return empty array
  if (plan === null) {
    return []
  }
  
  // Otherwise return array with the single calculated plan
  return [plan]
}

